import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/base/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";
import HeroSection from "./components/HeroSection";
import TrendingTicker from "./components/TrendingTicker";
import EventGrid from "./components/EventGrid";
import DecodeShowcase from "./components/DecodeShowcase";
import TimelineSection from "./components/TimelineSection";
import CategorySection from "./components/CategorySection";
import RankSection from "./components/RankSection";
import FaqSection from "./components/FaqSection";
import { events, faqs, categories } from "@/mocks/gossip";

const principles = [
  {
    icon: "ri-links-line",
    title: "来源可追溯",
    desc: "每个时间节点都会标注材料来源与可信度等级，模糊或无法核实的部分会明确标出。",
  },
  {
    icon: "ri-git-branch-line",
    title: "前因后果对齐",
    desc: "不做片段式转述，把背景、导火索、发酵过程与当前状态放在同一条时间线上对照。",
  },
  {
    icon: "ri-scales-3-line",
    title: "不做主观定性",
    desc: "我们只整理信息，不替读者下结论。存在争议的地方，会把双方说法并列呈现。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续修订",
    desc: "出现新节点就补进时间线；已发布内容如有错误，会在页面标注修订记录并同步更正。",
  },
];

export default function Home() {
  usePageMeta("91吃瓜网 - 娱乐事件深扒时间线复盘与关系图解码", {
    description:
      "91吃瓜网专注娱乐事件深扒复盘：按时间线梳理前因后果，整理实锤证据并解码人物关系图，帮你把复杂瓜料看个明明白白。",
    keywords: "91吃瓜网,深扒事件,时间线复盘,前因后果,实锤整理,关系图解码,娱乐八卦",
    canonicalPath: "/",
    ogType: "website",
  });

  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "91吃瓜网",
      alternateName: "91吃瓜网深扒档案室",
      description: "娱乐事件深扒时间线复盘与关系图解码门户",
      inLanguage: "zh-CN",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://249p.cn/events?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "91吃瓜网",
      description: "以时间线、前因后果与关系图整理娱乐事件的信息门户",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "contact@91chigua-daily.example",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "91吃瓜网深扒复盘首页",
      description: "汇总最新深扒事件时间线复盘、关系图解码与实锤整理内容",
      hasPart: events.map((e) => ({
        "@type": "Article",
        headline: e.title,
        description: e.summary,
        datePublished: e.date,
        articleSection: e.category,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "主题分类",
      itemListElement: categories.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        url: `https://249p.cn/category/${c.slug}`,
      })),
    },
  ];

  return (
    <>
      <JsonLd data={homeJsonLd} />
      <SiteHeader />
      <main className="w-full">
        <HeroSection event={events[0]} />
        <TrendingTicker />
        <EventGrid />
        <DecodeShowcase />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.25em] text-primary-600 uppercase">
                  <span className="w-6 h-px bg-primary-500"></span>
                  内容原则
                </span>
                <h2 className="mt-3 font-heading font-bold text-2xl md:text-[32px] leading-tight text-foreground-950">
                  吃瓜可以，但信息要立得住
                </h2>
                <p className="mt-3 font-label text-sm md:text-[15px] leading-relaxed text-foreground-600">
                  我们相信，把材料摊开比给一个结论更有价值。这四条是我们做每一次复盘时的底线。
                </p>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {principles.map((p) => (
                <article key={p.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{p.title}</h3>
                  <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <TimelineSection />
        <CategorySection />
        <RankSection />
        <FaqSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}